import { createClient, SupabaseClient, User } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// In development, if no keys are provided, use dummy values
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
const defaultUrl = 'http://localhost:54321'
const defaultAnonKey = 'dummy-key-for-development'

// Use real values if available, otherwise use defaults in development
const finalUrl = supabaseUrl || (isDevelopment ? defaultUrl : '')
const finalAnonKey = supabaseAnonKey || (isDevelopment ? defaultAnonKey : '')

console.log('Initializing Supabase client with URL:', finalUrl)
// Don't log the full keys, just first few chars
if (finalAnonKey) {
  console.log('Supabase Anon Key:', finalAnonKey.substring(0, 10) + '...')
}

// Regular client for normal operations
export const supabase: SupabaseClient = createClient(finalUrl, finalAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'implicit',
    storage: {
      getItem: (key) => {
        try {
          const storedValue = window.localStorage.getItem(key)
          return storedValue
        } catch (error) {
          console.error('Error accessing localStorage:', error)
          return null
        }
      },
      setItem: (key, value) => {
        try {
          window.localStorage.setItem(key, value)
        } catch (error) {
          console.error('Error setting localStorage:', error)
        }
      },
      removeItem: (key) => {
        try {
          window.localStorage.removeItem(key)
        } catch (error) {
          console.error('Error removing from localStorage:', error)
        }
      }
    }
  }
})

// List of allowed admins
export const ALLOWED_ADMINS: readonly string[] = [
  'philip@predicthire.com',
  'info@predicthire.com',
  'philipleth@gmail.com',
  'husseinumar@me.com',
  'frederik@openocean.dk',
  'jonni.n@gmail.com',
] as const

// Check if user is logged in and is in allowed list
export async function isAuthenticated(): Promise<boolean> {
  if (isDevelopment) {
    return true // Always authenticated in development
  }

  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user?.email) {
    console.log('No session found')
    return false
  }
  const isAdmin = isAllowedAdmin(session.user.email)
  console.log('User email:', session.user.email, 'Is admin:', isAdmin)
  return isAdmin
}

// Get current user
export async function getCurrentUser() {
  if (isDevelopment) {
    const devUser: User = {
      id: 'dev-user',
      email: ALLOWED_ADMINS[0],
      app_metadata: {},
      user_metadata: {},
      aud: 'authenticated',
      created_at: new Date().toISOString(),
      role: 'authenticated',
      updated_at: new Date().toISOString()
    }
    return devUser
  }

  const { data: { session } } = await supabase.auth.getSession()
  return session?.user || null
}

// Sign out
export async function signOut() {
  if (isDevelopment) {
    return // No-op in development
  }
  return supabase.auth.signOut()
}

// Sign in with magic link
export async function signIn(email: string) {
  if (isDevelopment) {
    return // No-op in development
  }

  const redirectTo = window.location.origin
  console.log('Signing in with redirect to:', redirectTo)
  
  try {
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo,
        shouldCreateUser: true,
        data: {
          email_confirmed: true
        }
      }
    })
    
    if (error) {
      // Check for rate limiting error
      if (error.message.toLowerCase().includes('rate limit') || error.message.toLowerCase().includes('too many requests')) {
        throw new Error('Please wait a minute before requesting another magic link.')
      }
      throw error
    }
    
    return data
  } catch (error: any) {
    console.error('Error sending magic link:', error.message)
    throw error
  }
}

// Development-only direct sign in (no-op since we're bypassing auth in development)
export async function signInDev(email: string) {
  if (!isDevelopment) {
    throw new Error('Development sign in only allowed on localhost')
  }
  
  // Return a mock session data structure similar to what Supabase would return
  return {
    user: {
      id: 'dev-user',
      email: email,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  }
}

export const isAllowedAdmin = (email: string): boolean => {
  if (isDevelopment) return true
  // Check if email ends with @predicthire.com or is in the allowed list
  return email.endsWith('@predicthire.com') || ALLOWED_ADMINS.includes(email)
}

export const lookupCompanyInCVR = async (leadId: string) => {
  const { data, error } = await supabase.functions.invoke('check-cvr-data', {
    body: { 
      lead_ids: [leadId],
      batch_size: 1
    }
  });

  if (error) {
    throw error;
  }

  const result = data?.results?.details?.[0];
  if (!result) {
    throw new Error('No data returned from CVR lookup');
  }

  return {
    success: !result.error,
    message: result.error || 'Successfully fetched CVR data',
    data: result.data // Return the raw API response data
  };
};